import React, { useEffect, useState, useMemo } from "react";
import { http } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Monitor, Plus, Download, Upload, Eye, Edit, Trash2, Server, Loader, Search, QrCode, Building2, MapPin, ChevronRight, ArrowLeft, Layers, Users, CheckCircle } from 'lucide-react';
import EquipoForm from "./EquipoForm";
import ImportarEquipos from "./ImportarEquipos";
import EquipoPreview from "./EquipoPreview";
import SmartAdaptiveList from "../ui/SmartAdaptiveList";
import ResponsiveModal from "../ui/ResponsiveModal";
import EquipoQRCode from "./EquipoQRCode";
import QRScanner from "./QRScanner";

// Recolecta y normaliza variantes de IDs (UUID, ObjectId, etc.) en formato string único
const gatherIdVariants = (...values) => {
  const result = new Set();

  const visit = (value) => {
    if (!value) return;

    if (value instanceof Set) {
      value.forEach(visit);
      return;
    }

    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }

    const valueType = typeof value;
    if (valueType === "string" || valueType === "number") {
      const strValue = String(value).trim();
      if (strValue) {
        result.add(strValue);
      }
      return;
    }

    if (valueType === "object") {
      const keysToInspect = [
        "id",
        "_id",
        "mongo_id",
        "mongoId",
        "cliente_id",
        "clienteId",
        "uuid",
        "uuid_cliente",
        "primaryId",
        "value"
      ];
      keysToInspect.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(value, key)) {
          visit(value[key]);
        }
      });
    }
  };

  values.forEach(visit);
  return Array.from(result);
};

export default function EquiposList({ equipoIdFromUrl, onEquipoViewed }) {
  const { user } = useAuth();
  const [equipos, setEquipos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [equipoEditando, setEquipoEditando] = useState(null);
  const [mostrarImportacion, setMostrarImportacion] = useState(false);
  const [equipoPreview, setEquipoPreview] = useState(null);
  const [mostrarQRCode, setMostrarQRCode] = useState(false);
  const [equipoQR, setEquipoQR] = useState(null);
  const [mostrarQRScanner, setMostrarQRScanner] = useState(false);

  // Navegación jerárquica
  const [vista, setVista] = useState('clientes');       // 'clientes' | 'sucursales' | 'equipos'
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState(null);
  const [busquedaLocal, setBusquedaLocal] = useState('');
  const [busquedaCliente, setBusquedaCliente] = useState('');

  useEffect(() => {
    if (user?.rol === "admin" || user?.rol === "tecnico") {
      cargarEquipos();
      cargarClientes();
    }
  }, [user]);
  
  // Efecto para abrir el equipo automáticamente si viene de URL
  useEffect(() => {
    if (equipoIdFromUrl && equipos.length > 0) {
      const equipo = equipos.find(eq => eq._id === equipoIdFromUrl || eq.id === equipoIdFromUrl);
      if (equipo) {
        setEquipoPreview(equipo);
        // Limpiar la URL
        window.history.replaceState({}, '', '/');
        // Notificar que ya se vio el equipo
        if (onEquipoViewed) {
          onEquipoViewed();
        }
      }
    }
  }, [equipoIdFromUrl, equipos, onEquipoViewed]);

  async function cargarEquipos() {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await http.get("/equipos/", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const equiposData = Array.isArray(res.data) ? res.data : [];
      const equiposConId = equiposData.map((equipo) => {
        const uuid = equipo.id ? String(equipo.id) : '';
        const objectId = equipo._id ? String(equipo._id) : '';
        const mongoId = equipo.mongo_id ? String(equipo.mongo_id) : '';
        const resolvedId = uuid || objectId || mongoId;

        const clienteInfo = equipo.cliente
          ? {
              ...equipo.cliente,
              lookupIds: gatherIdVariants(
                equipo.cliente,
                equipo.cliente?.lookupIds,
                equipo.cliente?.id,
                equipo.cliente?._id,
                equipo.cliente?.mongo_id,
                equipo.cliente?.mongoId
              )
            }
          : undefined;

        const clienteLookupIds = gatherIdVariants(
          equipo.cliente_lookup_ids,
          equipo.cliente_id,
          equipo.clienteId,
          equipo.cliente_uuid,
          clienteInfo?.lookupIds
        );

        return {
          ...equipo,
          id: uuid || resolvedId,
          _id: objectId || resolvedId,
          cliente: clienteInfo,
          cliente_lookup_ids: clienteLookupIds
        };
      });
      setEquipos(equiposConId);
    } catch (err) {
      console.error("Error cargando equipos", err);
      setEquipos([]);
    } finally {
      setLoading(false);
    }
  }

  async function cargarClientes() {
    try {
      const token = localStorage.getItem("token");
      const res = await http.get("/clientes", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const clientesData = Array.isArray(res.data) ? res.data : [];
      
      // Ordenar clientes alfabéticamente
      clientesData.sort((a, b) => (a.nombre || "").toLowerCase().localeCompare((b.nombre || "").toLowerCase()));

      const clientesNormalizados = clientesData.map((cliente) => {
        const lookupIds = gatherIdVariants(
          cliente.lookupIds,
          cliente.primaryId,
          cliente.id,
          cliente._id,
          cliente.mongo_id,
          cliente.mongoId,
          cliente.cliente_id,
          cliente.clienteId
        );
        const primaryId = cliente.primaryId || lookupIds[0] || '';
        return {
          ...cliente,
          primaryId,
          lookupIds
        };
      });
      setClientes(clientesNormalizados);
    } catch (err) {
      console.error("Error cargando clientes", err);
      setClientes([]);
    }
  }

  async function eliminarEquipo(id) {
    if (!id) {
      alert("❌ ID de equipo inválido");
      return;
    }
    
    if (!window.confirm("¿Estás seguro de eliminar este equipo?")) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await http.delete(`/equipos/${id}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      alert("✅ Equipo eliminado correctamente");
      await cargarEquipos();
    } catch (err) {
      console.error("Error eliminando equipo:", err);
      const mensaje = err.response?.data?.detail || "Error al eliminar equipo";
      alert(`❌ ${mensaje}`);
    }
  }

  // Función para manejar el QR escaneado
  async function handleQRScanSuccess(equipoId) {
    setMostrarQRScanner(false);
    
    try {
      const token = localStorage.getItem("token");
      const res = await http.get(`/equipos/${equipoId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (res.data) {
        // Mostrar el preview del equipo escaneado
        setEquipoPreview(res.data);
      }
    } catch (err) {
      console.error('Error al cargar equipo escaneado:', err);
      alert('❌ No se pudo cargar los detalles del equipo escaneado');
    }
  }

  // Función para mostrar el QR de un equipo
  function mostrarQREquipo(equipo) {
    setEquipoQR(equipo);
    setMostrarQRCode(true);
  }

  async function importarCSV(e) {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const token = localStorage.getItem("token");

      // 1. Crear clientes automáticamente
      await http.post("/clientes/importar-desde-csv", formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // 2. Importar equipos
      await http.post("/equipos/importar-csv", formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("✅ Equipos importados correctamente");
      await cargarEquipos();
    } catch (err) {
      console.error("Error importando CSV", err);
      alert("❌ Error al importar CSV");
    }
  }

  function exportarCSV() {
    const csvContent = [
      [
        "cliente_nombre",
        "sucursal",
        "marca",
        "modelo",
        "tipo_equipo",
        "numero_serie",
        "procesador",
        "ram",
        "disco",
        "tipo_disco",
        "estado",
        "ubicacion",
        "observaciones"
      ],
      ...equipos.map((eq) => [
        eq.cliente_id || "",
        eq.sucursal || "",
        eq.marca || "",
        eq.modelo || "",
        eq.tipo_equipo || "",
        eq.numero_serie || "",
        eq.procesador || "",
        eq.ram || "",
        eq.disco || "",
        eq.tipo_disco || "",
        eq.estado || "operativo",
        eq.ubicacion || "",
        eq.observaciones || ""
      ])
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "equipos_exportados.csv";
    link.click();
  }

  // ─── Helpers de navegación jerárquica ───────────────────────────────────

  function equipoEsDeCliente(eq, cliente) {
    if (!cliente) return false;
    const clienteIds = new Set(cliente.lookupIds || []);
    const eqIds = new Set(gatherIdVariants(
      eq.cliente_lookup_ids, eq.cliente_id, eq.clienteId,
      eq.cliente_uuid, eq.cliente, eq.cliente?.lookupIds
    ));
    return [...clienteIds].some(id => eqIds.has(id));
  }

  // Equipos indexados por primaryId del cliente
  const equiposPorClienteMap = useMemo(() => {
    const map = {};
    equipos.forEach(eq => {
      const match = clientes.find(c => equipoEsDeCliente(eq, c));
      const key = match?.primaryId || '__sin_cliente__';
      if (!map[key]) map[key] = [];
      map[key].push(eq);
    });
    return map;
  }, [equipos, clientes]);

  // Sucursales efectivas del cliente seleccionado
  const sucursalesDelCliente = useMemo(() => {
    if (!clienteSeleccionado) return [];
    const fromClientData = (clienteSeleccionado.sucursales || [])
      .map(s => (typeof s === 'string' ? s : s?.nombre))
      .filter(Boolean);
    const fromEquipos = [...new Set(
      (equiposPorClienteMap[clienteSeleccionado.primaryId] || [])
        .map(eq => eq.sucursal).filter(Boolean)
    )];
    return [...new Set([...fromClientData, ...fromEquipos])];
  }, [clienteSeleccionado, equiposPorClienteMap]);

  // Equipos visibles en la vista actual
  const equiposActuales = useMemo(() => {
    if (vista !== 'equipos' || !clienteSeleccionado) return [];
    let base = equiposPorClienteMap[clienteSeleccionado.primaryId] || [];
    if (sucursalSeleccionada) base = base.filter(eq => eq.sucursal === sucursalSeleccionada);
    if (busquedaLocal.trim()) {
      const t = busquedaLocal.toLowerCase();
      base = base.filter(eq =>
        [eq.nombre, eq.marca, eq.modelo, eq.numero_serie, eq.ubicacion, eq.tipo_equipo]
          .some(v => v && String(v).toLowerCase().includes(t))
      );
    }
    return base;
  }, [vista, clienteSeleccionado, sucursalSeleccionada, busquedaLocal, equiposPorClienteMap]);

  // Equipos agrupados por tipo dentro de la vista actual
  const equiposPorTipo = useMemo(() => {
    const grupos = {};
    equiposActuales.forEach(eq => {
      const tipo = eq.tipo_equipo || 'Sin tipo';
      if (!grupos[tipo]) grupos[tipo] = [];
      grupos[tipo].push(eq);
    });
    return Object.entries(grupos).sort(([a], [b]) => a.localeCompare(b));
  }, [equiposActuales]);

  // Clientes filtrados para la vista de selección
  const clientesFiltrados = useMemo(() => {
    const hayOrfanos = (equiposPorClienteMap['__sin_cliente__'] || []).length > 0;
    const lista = clientes.filter(c =>
      !busquedaCliente.trim() ||
      (c.nombre || '').toLowerCase().includes(busquedaCliente.toLowerCase())
    );
    if (hayOrfanos && (!busquedaCliente.trim() || 'sin cliente'.includes(busquedaCliente.toLowerCase()))) {
      return [...lista, { primaryId: '__sin_cliente__', nombre: 'Sin cliente asignado', lookupIds: [], _virtual: true }];
    }
    return lista;
  }, [clientes, busquedaCliente, equiposPorClienteMap]);

  function abrirCliente(cliente) {
    setClienteSeleccionado(cliente);
    setBusquedaLocal('');
    const sucData = (cliente.sucursales || []).map(s => (typeof s === 'string' ? s : s?.nombre)).filter(Boolean);
    const sucEquipos = [...new Set((equiposPorClienteMap[cliente.primaryId] || []).map(eq => eq.sucursal).filter(Boolean))];
    const todas = [...new Set([...sucData, ...sucEquipos])];
    if (todas.length > 0) { setSucursalSeleccionada(null); setVista('sucursales'); }
    else { setSucursalSeleccionada(null); setVista('equipos'); }
  }

  function abrirSucursal(suc) {
    setSucursalSeleccionada(suc);
    setBusquedaLocal('');
    setVista('equipos');
  }

  function volverAClientes() {
    setClienteSeleccionado(null); setSucursalSeleccionada(null);
    setVista('clientes'); setBusquedaLocal(''); setBusquedaCliente('');
  }

  function volverASucursales() {
    setSucursalSeleccionada(null); setBusquedaLocal(''); setVista('sucursales');
  }

  // equiposFiltrados: todos los equipos (usado en exportarCSV que descarga todo)
  const equiposFiltrados = equipos;

  if (!(user?.rol === "admin" || user?.rol === "tecnico")) {
    return (
      <div className="flex items-center justify-center min-h-64 bg-gray-50 rounded-lg">
        <div className="text-center p-6">
          <Monitor className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">⚠️ No tienes permisos para ver equipos</p>
        </div>
      </div>
    );
  }

  // ── helpers de fila de equipo ────────────────────────────────────────────
  const estadoBadge = (eq) => {
    const op = eq.estado === 'operativo' || eq.estado === 'activo';
    const mant = eq.estado === 'mantenimiento' || eq.estado === 'en_reparacion';
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
        op ? 'bg-green-100 text-green-800' : mant ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
      }`}>{eq.estado || '—'}</span>
    );
  };

  const botonesAccion = (eq) => (
    <div className="flex gap-1 justify-center">
      <button className="p-1.5 text-teal-600 hover:bg-teal-50 rounded-lg" onClick={() => mostrarQREquipo(eq)} title="QR"><QrCode className="w-4 h-4"/></button>
      <button className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg" onClick={() => setEquipoPreview(eq)} title="Ver"><Eye className="w-4 h-4"/></button>
      <button className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded-lg" onClick={() => setEquipoEditando({...eq, _id: eq.id})} title="Editar"><Edit className="w-4 h-4"/></button>
      <button className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg" onClick={() => eliminarEquipo(eq._id)} title="Eliminar"><Trash2 className="w-4 h-4"/></button>
    </div>
  );

  return (
    <div className="bg-white p-4 md:p-6 rounded-lg shadow">

      {/* ── Modales (siempre presentes) ────────────────────────────────── */}
      <ResponsiveModal isOpen={mostrarImportacion} onClose={() => setMostrarImportacion(false)} title="Importar Equipos desde GLPI" size="large">
        <ImportarEquipos onClose={() => setMostrarImportacion(false)} onImportComplete={() => { setMostrarImportacion(false); cargarEquipos(); }} />
      </ResponsiveModal>

      {/* EquipoForm maneja su propio portal */}
      {(mostrarFormulario || !!equipoEditando) && (
        <EquipoForm equipo={equipoEditando}
          onGuardado={() => { cargarEquipos(); setMostrarFormulario(false); setEquipoEditando(null); }}
          onCancelar={() => { setMostrarFormulario(false); setEquipoEditando(null); }} />
      )}

      {/* EquipoPreview maneja su propio portal */}
      {equipoPreview && <EquipoPreview equipo={equipoPreview} onClose={() => setEquipoPreview(null)} />}

      {equipoQR && <EquipoQRCode equipoId={equipoQR._id || equipoQR.id} equipoNombre={equipoQR.nombre || equipoQR.descripcion} qrCode={equipoQR.qr_code} isOpen={mostrarQRCode} onClose={() => { setMostrarQRCode(false); setEquipoQR(null); }} />}
      <QRScanner isOpen={mostrarQRScanner} onClose={() => setMostrarQRScanner(false)} onSuccess={handleQRScanSuccess} />

      {/* ── Header permanente ──────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          {vista === 'clientes' && (
            <h2 className="text-xl font-bold flex items-center gap-2 text-gray-800">
              <Monitor className="w-5 h-5 text-emerald-600" />Equipos por cliente
            </h2>
          )}
          {vista === 'sucursales' && (
            <div>
              <button onClick={volverAClientes} className="flex items-center gap-1 text-sm text-emerald-600 hover:text-emerald-800 mb-1">
                <ArrowLeft className="w-4 h-4" />Todos los clientes
              </button>
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-600" />{clienteSeleccionado?.nombre}
              </h2>
            </div>
          )}
          {vista === 'equipos' && (
            <div>
              <div className="flex items-center gap-1 text-sm text-gray-400 mb-1 flex-wrap">
                <button onClick={volverAClientes} className="hover:text-emerald-600 transition-colors">Clientes</button>
                <ChevronRight className="w-3 h-3" />
                {sucursalesDelCliente.length > 0
                  ? <button onClick={volverASucursales} className="hover:text-emerald-600 transition-colors">{clienteSeleccionado?.nombre}</button>
                  : <span>{clienteSeleccionado?.nombre}</span>}
                {sucursalSeleccionada && <><ChevronRight className="w-3 h-3" /><span className="text-gray-600">{sucursalSeleccionada}</span></>}
              </div>
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <Layers className="w-5 h-5 text-emerald-600" />
                {sucursalSeleccionada || 'Todas las sucursales'}
                <span className="text-base font-normal text-gray-400">— {clienteSeleccionado?.nombre}</span>
              </h2>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <button onClick={() => setMostrarQRScanner(true)} className="inline-flex items-center gap-1.5 px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm">
            <QrCode className="w-4 h-4" /><span className="hidden sm:inline">Escanear QR</span>
          </button>
          <button onClick={() => setMostrarFormulario(true)} className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm">
            <Plus className="w-4 h-4" />Nuevo Equipo
          </button>
          <button onClick={exportarCSV} className="inline-flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
            <Download className="w-4 h-4" /><span className="hidden sm:inline">Exportar CSV</span>
          </button>
          <button onClick={() => setMostrarImportacion(true)} className="inline-flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm">
            <Upload className="w-4 h-4" /><span className="hidden sm:inline">Importar GLPI</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <Loader className="w-8 h-8 animate-spin text-emerald-600" />
          <span className="ml-3 text-gray-600">Cargando equipos...</span>
        </div>
      ) : (
        <>
          {/* ══ VISTA: CLIENTES ══════════════════════════════════════════ */}
          {vista === 'clientes' && (
            <>
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-emerald-700">{equipos.length}</div>
                  <div className="text-xs text-emerald-600 font-medium mt-0.5">Total equipos</div>
                </div>
                <div className="bg-green-50 border border-green-100 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-green-700">{equipos.filter(e => e.estado === 'operativo' || e.estado === 'activo').length}</div>
                  <div className="text-xs text-green-600 font-medium mt-0.5">Operativos</div>
                </div>
                <div className="bg-purple-50 border border-purple-100 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-purple-700">{clientes.length}</div>
                  <div className="text-xs text-purple-600 font-medium mt-0.5">Clientes</div>
                </div>
              </div>

              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" value={busquedaCliente} onChange={e => setBusquedaCliente(e.target.value)}
                  placeholder="Buscar cliente..." className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm" />
              </div>

              {clientesFiltrados.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Users className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>No se encontraron clientes</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {clientesFiltrados.map(cliente => {
                    const count = (equiposPorClienteMap[cliente.primaryId] || []).length;
                    return (
                      <button key={cliente.primaryId} onClick={() => abrirCliente(cliente)}
                        className={`text-left p-4 rounded-xl border-2 transition-all hover:shadow-md hover:-translate-y-0.5 group ${
                          cliente._virtual ? 'border-gray-200 bg-gray-50 hover:border-gray-400' : 'border-gray-100 bg-white hover:border-emerald-300'
                        }`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${cliente._virtual ? 'bg-gray-200' : 'bg-emerald-100'}`}>
                              <Building2 className={`w-4 h-4 ${cliente._virtual ? 'text-gray-500' : 'text-emerald-600'}`} />
                            </div>
                            <div className="min-w-0">
                              <div className="font-semibold text-gray-800 text-sm truncate leading-tight">{cliente.nombre}</div>
                              {cliente.rubro && <div className="text-xs text-gray-400 truncate">{cliente.rubro}</div>}
                            </div>
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <span className={`text-xl font-bold leading-none ${count > 0 ? 'text-emerald-600' : 'text-gray-300'}`}>{count}</span>
                            <div className="text-xs text-gray-400">eq.</div>
                          </div>
                        </div>
                        {(cliente.sucursales?.length > 0) && (
                          <div className="mt-2 flex items-center gap-1 text-xs text-gray-400">
                            <MapPin className="w-3 h-3" />{cliente.sucursales.length} sucursal{cliente.sucursales.length !== 1 ? 'es' : ''}
                          </div>
                        )}
                        <div className="mt-2 flex items-center justify-end text-xs text-emerald-400 group-hover:text-emerald-600 font-medium transition-colors">
                          Ver equipos <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* ══ VISTA: SUCURSALES ════════════════════════════════════════ */}
          {vista === 'sucursales' && clienteSeleccionado && (
            <>
              <div className="mb-4 p-4 bg-emerald-50 border border-emerald-100 rounded-xl flex items-center gap-4">
                <div className="w-11 h-11 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <div className="font-bold text-gray-800">{clienteSeleccionado.nombre}</div>
                  <div className="text-sm text-gray-500">{(equiposPorClienteMap[clienteSeleccionado.primaryId] || []).length} equipos en total · {sucursalesDelCliente.length} sucursal{sucursalesDelCliente.length !== 1 ? 'es' : ''}</div>
                </div>
              </div>

              <div className="space-y-2">
                <button onClick={() => abrirSucursal(null)}
                  className="w-full text-left flex items-center justify-between p-4 rounded-xl border-2 border-emerald-100 bg-white hover:border-emerald-400 hover:shadow-sm transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center"><Layers className="w-4 h-4 text-emerald-600" /></div>
                    <div>
                      <div className="font-semibold text-gray-800 text-sm">Todas las sucursales</div>
                      <div className="text-xs text-gray-500">{(equiposPorClienteMap[clienteSeleccionado.primaryId] || []).length} equipos</div>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>

                {sucursalesDelCliente.map(suc => {
                  const cnt = (equiposPorClienteMap[clienteSeleccionado.primaryId] || []).filter(eq => eq.sucursal === suc).length;
                  return (
                    <button key={suc} onClick={() => abrirSucursal(suc)}
                      className="w-full text-left flex items-center justify-between p-4 rounded-xl border-2 border-gray-100 bg-white hover:border-emerald-300 hover:shadow-sm transition-all">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center"><MapPin className="w-4 h-4 text-gray-600" /></div>
                        <div>
                          <div className="font-semibold text-gray-800 text-sm">{suc}</div>
                          <div className="text-xs text-gray-500">{cnt} equipo{cnt !== 1 ? 's' : ''}</div>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* ══ VISTA: EQUIPOS (agrupados por tipo) ═════════════════════ */}
          {vista === 'equipos' && (
            <>
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" value={busquedaLocal} onChange={e => setBusquedaLocal(e.target.value)}
                  placeholder="Buscar por nombre, marca, modelo o serie..." className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm" />
              </div>

              {equiposActuales.length === 0 ? (
                <div className="text-center py-14 text-gray-400">
                  <Server className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p className="font-medium">{busquedaLocal ? 'Sin resultados para la búsqueda' : 'No hay equipos en esta sección'}</p>
                  <button onClick={() => setMostrarFormulario(true)} className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700">
                    <Plus className="w-4 h-4" />Agregar equipo
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm text-gray-500 pb-2 border-b">
                    <span><span className="font-semibold text-gray-700">{equiposActuales.length}</span> equipo{equiposActuales.length !== 1 ? 's' : ''}{busquedaLocal && ` · "${busquedaLocal}"`}</span>
                    <span className="flex items-center gap-1 text-green-600">
                      <CheckCircle className="w-3.5 h-3.5" />
                      {equiposActuales.filter(e => e.estado === 'operativo' || e.estado === 'activo').length} operativos
                    </span>
                  </div>

                  {equiposPorTipo.map(([tipo, items]) => (
                    <div key={tipo} className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                        <Layers className="w-4 h-4 text-gray-500" />
                        <span className="font-semibold text-gray-700 text-sm">{tipo}</span>
                        <span className="ml-auto text-xs font-medium bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">{items.length}</span>
                      </div>

                      {/* Tabla desktop */}
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-white border-b border-gray-100">
                            <tr>
                              {['Equipo', 'Marca / Modelo', 'Sucursal / Ubicación', 'Estado', 'Acciones'].map(h => (
                                <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-50">
                            {items.map(eq => (
                              <tr key={eq._id || eq.id} className="hover:bg-gray-50">
                                <td className="px-3 py-3">
                                  <div className="flex items-center gap-2.5">
                                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                      <Monitor className="w-4 h-4 text-emerald-600" />
                                    </div>
                                    <div>
                                      <div className="font-medium text-gray-900 text-sm">{eq.nombre || eq.descripcion || '—'}</div>
                                      <div className="text-xs text-gray-400 font-mono">{eq.numero_serie || ''}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-3 py-3">
                                  <div className="text-sm font-medium text-gray-800">{eq.marca} {eq.modelo}</div>
                                  {eq.procesador && <div className="text-xs text-gray-400 truncate max-w-[180px]">{eq.procesador}</div>}
                                </td>
                                <td className="px-3 py-3">
                                  {eq.sucursal ? (
                                    <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">
                                      <MapPin className="w-3 h-3" />{eq.sucursal}
                                    </span>
                                  ) : <span className="text-xs text-gray-300">—</span>}
                                  {eq.ubicacion && <div className="text-xs text-gray-400 mt-0.5">{eq.ubicacion}</div>}
                                </td>
                                <td className="px-3 py-3">{estadoBadge(eq)}</td>
                                <td className="px-3 py-3">{botonesAccion(eq)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Tarjetas mobile */}
                      <div className="md:hidden bg-white divide-y divide-gray-100">
                        {items.map(eq => (
                          <div key={eq._id || eq.id} className="p-4">
                            <div className="flex items-start justify-between gap-2 mb-3">
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <Monitor className="w-4 h-4 text-emerald-600" />
                                </div>
                                <div className="min-w-0">
                                  <div className="font-semibold text-sm text-gray-900 truncate">{eq.nombre || '—'}</div>
                                  <div className="text-xs text-gray-500">{eq.marca} {eq.modelo}</div>
                                </div>
                              </div>
                              {estadoBadge(eq)}
                            </div>
                            {eq.sucursal && (
                              <div className="flex items-center gap-1 text-xs text-emerald-600 mb-2">
                                <MapPin className="w-3 h-3" />{eq.sucursal}
                              </div>
                            )}
                            <div className="flex gap-1.5 pt-2 border-t border-gray-100">
                              <button className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs bg-teal-50 text-teal-700 rounded-lg" onClick={() => mostrarQREquipo(eq)}><QrCode className="w-3.5 h-3.5"/>QR</button>
                              <button className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs bg-emerald-50 text-emerald-700 rounded-lg" onClick={() => setEquipoPreview(eq)}><Eye className="w-3.5 h-3.5"/>Ver</button>
                              <button className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs bg-yellow-50 text-yellow-700 rounded-lg" onClick={() => setEquipoEditando({...eq, _id: eq.id})}><Edit className="w-3.5 h-3.5"/>Editar</button>
                              <button className="px-3 py-1.5 text-xs bg-red-50 text-red-700 rounded-lg" onClick={() => eliminarEquipo(eq._id)}><Trash2 className="w-3.5 h-3.5"/></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

